/**
 * Campaign display helpers
 *
 * The `campaign` field on a job/delivery doc is typically a plain string, but
 * in legacy data and certain code paths it can end up as an `{ label, value }`
 * object (from an Autocomplete control) or null. Rendering an object directly
 * produces the dreaded `[object Object]` string. These helpers normalise to a
 * displayable string and fall back to a consistent placeholder when missing.
 */

export const CAMPAIGN_FALLBACK = 'Untitled Campaign';

/** Defensive display helper — accepts the campaign field in any of its
 *  observed shapes (string, Autocomplete option, null, undefined) and
 *  returns a safe string for rendering and for sort/search comparisons. */
export function getCampaignName(
  campaign: unknown,
  fallback: string = CAMPAIGN_FALLBACK,
): string {
  if (campaign == null) return fallback;
  if (typeof campaign === 'string') {
    const trimmed = campaign.trim();
    return trimmed || fallback;
  }
  if (typeof campaign === 'object') {
    const obj = campaign as { label?: unknown; name?: unknown; value?: unknown };
    const candidate = [obj.label, obj.name, obj.value]
      .find((v) => typeof v === 'string' && v.trim().length > 0);
    if (typeof candidate === 'string') return candidate.trim();
  }
  return fallback;
}
